/**
 * Per-track copilot reachability, derived from the relation tuples (HUB-087,
 * D28 of #218).
 *
 * The HUB-077 chat gate refuses any principal without `track#read`, and a
 * system admin bypasses it — so a track with no membership tuples looks
 * healthy to the admin viewing this page while every normal user is refused.
 * The tracks list states that condition outright.
 *
 * `system:hub` is the everyone-subject wildcard honored by the authz engine
 * (`authz/engine.ts:109-120`): one `track:<id>#<relation>@system:hub` tuple
 * grants that relation to every signed-in user. `viewer` is the sanctioned
 * "open to all hub users" posture, but ANY read-closure relation on that
 * subject opens the track — `member` and `admin` grant read and then some — so
 * all three are detected and the tier is surfaced rather than assumed.
 */
import type { HubClient } from '@verybigthings/trackframe-hub-client';
import type { RelationListItem } from '@verybigthings/trackframe-shared/schemas';

export const OPEN_TO_ALL_SUBJECT = 'system:hub';
/** The relation the "open to all" button GRANTS — read only, never write. */
export const OPEN_TO_ALL_RELATION = 'viewer';

/** Direct relations on `track` that feed `track#read`, widest access last. */
const READ_RELATIONS = ['viewer', 'member', 'admin'] as const;
export type TrackReadRelation = (typeof READ_RELATIONS)[number];

const READ_RELATION_SET = new Set<string>(READ_RELATIONS);

export interface TrackAccessState {
  /** Distinct subjects, excluding the wildcard, holding a read relation. */
  peopleCount: number;
  /**
   * Read-closure relations held by `system:hub`, tier-ordered (widest first).
   * Empty when the track is not open to everyone. Every entry is retained —
   * revoking must delete them ALL, or the track stays open through the one
   * left behind.
   */
  openToAllRelations: TrackReadRelation[];
}

export const NO_ACCESS: TrackAccessState = { peopleCount: 0, openToAllRelations: [] };

export function isOpenToAll(state: TrackAccessState): boolean {
  return state.openToAllRelations.length > 0;
}

/** The widest relation every signed-in user holds, or null. */
export function openToAllTier(state: TrackAccessState): TrackReadRelation | null {
  return state.openToAllRelations[0] ?? null;
}

/** True when nobody but a system admin can use the copilot on this track. */
export function isLockedOut(state: TrackAccessState): boolean {
  return !isOpenToAll(state) && state.peopleCount === 0;
}

/**
 * Badge copy for an open track. `member`/`admin` are called out separately:
 * they grant WRITE to every signed-in user, which is a materially different
 * posture from the read-only one the button offers.
 */
export function openToAllBadgeLabel(tier: TrackReadRelation): string {
  if (tier === 'admin') return 'All users are admins';
  if (tier === 'member') return 'All users can write';
  return 'Open to all users';
}

/**
 * Fold `GET /relations?objectType=track` rows into one state per track id.
 * Tuples against computed relations (`read`, `write`) are ignored: the engine
 * never consults them, so counting them would overstate access.
 */
export function deriveTrackAccess(relations: RelationListItem[]): Map<string, TrackAccessState> {
  const subjectsByTrack = new Map<string, Set<string>>();
  const wildcardByTrack = new Map<string, Set<string>>();

  for (const row of relations) {
    if (!row.object.startsWith('track:')) continue;
    if (!READ_RELATION_SET.has(row.relation)) continue;
    const trackId = row.object.slice('track:'.length);
    const bucket = row.subject === OPEN_TO_ALL_SUBJECT ? wildcardByTrack : subjectsByTrack;

    let entries = bucket.get(trackId);
    if (!entries) {
      entries = new Set();
      bucket.set(trackId, entries);
    }
    entries.add(row.subject === OPEN_TO_ALL_SUBJECT ? row.relation : row.subject);
  }

  const state = new Map<string, TrackAccessState>();
  for (const trackId of new Set([...subjectsByTrack.keys(), ...wildcardByTrack.keys()])) {
    const wildcard = wildcardByTrack.get(trackId) ?? new Set<string>();
    state.set(trackId, {
      peopleCount: subjectsByTrack.get(trackId)?.size ?? 0,
      // Widest first, so `openToAllTier` reads entry 0 and the badge names the
      // strongest thing every user can do.
      openToAllRelations: [...READ_RELATIONS].reverse().filter((r) => wildcard.has(r)),
    });
  }
  return state;
}

/**
 * The one mutation behind BOTH "open to all" surfaces — the tracks list card
 * and the track page's Access tab (HUB-109 D7). Opening writes the read-only
 * `viewer@system:hub` tuple. Closing deletes EVERY wildcard relation the track
 * holds, atomically: clearing only `viewer` would leave a `member`/`admin`
 * wildcard behind and the track would stay open while the UI claimed otherwise.
 */
export type OpenToAllClient = Pick<HubClient, 'relationsList' | 'relationsWrite' | 'relationsBatch'>;

export async function setTrackOpenToAll(hub: OpenToAllClient, trackId: string, open: boolean): Promise<void> {
  const object = `track:${trackId}`;
  if (open) {
    await hub.relationsWrite({
      body: { object, relation: OPEN_TO_ALL_RELATION, subject: OPEN_TO_ALL_SUBJECT },
    });
    return;
  }
  const res = await hub.relationsList({
    query: { objectType: 'track', objectId: trackId, includeStructural: false, limit: 500 },
  });
  const state = deriveTrackAccess(res.relations as RelationListItem[]).get(trackId) ?? NO_ACCESS;
  if (!isOpenToAll(state)) return;
  await hub.relationsBatch({
    body: {
      deletes: state.openToAllRelations.map((relation) => ({
        object,
        relation,
        subject: OPEN_TO_ALL_SUBJECT,
      })),
    },
  });
}
